import React, { useState, useEffect, useCallback } from "react";
import { Box, Card, CardHeader, CardContent, CardActions, Grid, Typography, Button, Chip, Checkbox, Dialog, DialogTitle, DialogContent, IconButton, Tab, Divider, DialogActions, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Add, RotateLeft, ViewKanban } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import styled from "@emotion/styled";
import { addUpTime, convertMinutesToHours } from "../utils/timeUtils";
import UserMenuChip from "./UserMenuChip";
import StatusChip from "./StatusChip";
// import PriorityChip from "./PriorityChip";
import UserAssignmentDialog from "./UserAssignmentDialog";
// import MarkdownDisplay from "./MarkdownDisplay";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsProjectLocked } from "../utils/lockUtils";
import { getTimers } from "../redux/ducks/time";
import RunningTimer from "./RunningTimer";
import { useDispatch, useSelector } from 'react-redux';
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { showAlert } from "../redux/slices/alerts";
import CreateTaskDialog from './CreateTaskDialog';
import { updateTaskStatus, updateTaskStatusWithBypass, completeTask, completeTaskWithBypass, deleteTask, restoreTask } from "../redux/ducks/task";
import TaskDetailsDialog from './TaskDetailsDialog';
import TimerButton from "./TimerButton";
import BoardGateWarningDialog from './BoardGateWarningDialog';
import { formatDate } from '../utils/dateUtils';
import { hasSkill, missingSkillHighlight } from '../utils/skillUtils';

const TaskCard = styled(Card)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 2',
    },
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
}));

const TaskCardHeader = styled(CardHeader)(({ theme }) => ({
    paddingBottom: theme.spacing(0),
}))

const TaskBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.text.disabled}`,
    marginBottom: theme.spacing(0),
    minHeight: theme.spacing(33),
    maxHeight: `calc(${theme.spacing(1)} * 16 + ${theme.spacing(2)} * 16)`,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
        width: '12px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.primary.light,
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-button': {
        display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
        border: `0px`,
        padding: theme.spacing(1, 0),
        minHeight: theme.spacing(0),
    }
}));

const TaskBoxItem = styled(Grid, { shouldForwardProp: (prop) => prop !== 'missingSkill' })(({ theme, missingSkill }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 0, 2),
    gap: theme.spacing(1),
    ...(missingSkill ? missingSkillHighlight(theme) : {}),
    [theme.breakpoints.up('sm')]: {
        gap: theme.spacing(2),
        flexDirection: 'row',
        alignItems: 'center',
        paddingBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gridTemplateRows: 'auto 1fr auto',
        gridTemplateAreas: '"check info board" "check info time" "actions actions actions"',
        alignItems: 'start',
        gap: 0,
        padding: theme.spacing(1.5),
        margin: theme.spacing(0, 0, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        '&:last-of-type': {
            marginBottom: 0,
        },
    }
}));

const TaskCheckbox = styled(Checkbox)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        gridArea: 'check',
        padding: theme.spacing(1),
        margin: theme.spacing(-1, 0, 0, -1),
    },
}));

const RestoreButton = styled(IconButton)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        gridArea: 'check',
        padding: theme.spacing(1.25),
        margin: theme.spacing(-1, 0, 0, -1),
    },
}));

const BoardButton = styled(IconButton)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        gridArea: 'board',
        justifySelf: 'end',
        margin: theme.spacing(-0.5, -0.5, 0, 1),
    },
}));

const TaskInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
        gridArea: 'info',
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
}));

const TaskActions = styled(Box)(({ theme }) => ({
    display: 'contents',
    [theme.breakpoints.down('sm')]: {
        gridArea: 'actions',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
        '& > .MuiButton-root': {
            marginLeft: 'auto',
        },
    },
}));

const TaskDivider = styled(Divider)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

const DeletedTaskInfo = styled(TaskInfo)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    textDecoration: 'line-through',
}));

const StyledTabList = styled(TabList)(({ theme }) => ({
    borderBottom: 1,
    borderColor: 'divider'
}));

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: theme.spacing(2, 1, 0),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1, 0, 0),
    },
}));

const CardTitleText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
}));

const WideButton = styled(Button)(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
}));

const TimeColumn = styled(Typography)(({ theme }) => ({
    minWidth: '50px',
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
        gridArea: 'time',
        justifySelf: 'end',
        marginLeft: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    paddingTop: theme.spacing(0),
}));

const TaskBoxItems = ({ task, setSelectedTask, onReAssign, activeTimer, onStatusUpdate, onStartTimer = null, onStopTimer = null, currentUserId, onCompleteTask, onReopenTask, onRestoreTask, isLocked = false, accountSlug, projectId }) => {
    const hasPermission = useHasPermission();
    const canCompleteTask = hasPermission('completeTasks');
    const canRestoreTask = hasPermission('restoreTasks');
    const canAssignTask = hasPermission("assignTask");
    const canSeeInternal = hasPermission('canSeeInternal');
    const highlightMissingSkill = canSeeInternal && hasPermission('editTasks') && !isLocked && !task.isDeleted && (!task.completedDate || hasPermission('editCompletedTasks')) && !hasSkill(task);
    const navigate = useNavigate();

    const openTaskDialog = (task) => {
        setSelectedTask(task);
    };

    const handleOpenBoard = (event) => {
        event.stopPropagation();
        if (!accountSlug || !projectId || !task?._id) return;
        navigate(`/account/${accountSlug}/project/${projectId}/task/${task._id}/board`);
    };

    const handleCheckboxChange = () => {
        if (task.completedDate) {
            onReopenTask(task);
        } else {
            onCompleteTask(task);
        }
    };

    return (
        <>
            <TaskBoxItem key={task._id} missingSkill={highlightMissingSkill}>
                {task.isDeleted ? (
                    <>
                        {canRestoreTask && !isLocked && (
                            <RestoreButton size="small" onClick={() => onRestoreTask(task)}>
                                <RotateLeft fontSize="small" />
                            </RestoreButton>
                        )}
                        <DeletedTaskInfo onClick={() => openTaskDialog(task)}>
                            <Typography variant="body1" component="span">
                                {task.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Due: {task.dueDate ? formatDate(task.dueDate) : "No due date"}
                                {task.completedDate !== null && ` - Completed: ${formatDate(task.completedDate)}`}
                            </Typography>
                        </DeletedTaskInfo>
                        <TimeColumn variant="body2">{task.time && task.time.total ? addUpTime(convertMinutesToHours(task.time.total)) + ` Hrs` : "0 Hrs"}</TimeColumn>
                        <TaskActions>
                            <Chip variant="outlined" size="small" color="error" label="Deleted" />
                        </TaskActions>
                    </>
                ) : (
                    <>
                        {canCompleteTask && !isLocked && <TaskCheckbox checked={!!task.completedDate} id={task._id} onChange={handleCheckboxChange} onClick={(e) => e.stopPropagation()} />}
                        {task.hasBoard && canSeeInternal && (
                            <Tooltip title="Open QA Board">
                                <BoardButton size="small" onClick={handleOpenBoard} aria-label="Open QA Board">
                                    <ViewKanban fontSize="medium" />
                                </BoardButton>
                            </Tooltip>
                        )}
                        <TaskInfo onClick={() => openTaskDialog(task)}>
                            <Typography variant="body1" component="span">
                                {task.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Due: {task.dueDate ? formatDate(task.dueDate) : "No due date"}
                                {task.completedDate !== null && ` - Completed: ${formatDate(task.completedDate)}`}
                            </Typography>
                        </TaskInfo>
                        <TimeColumn variant="body2">
                            {activeTimer && activeTimer.user === currentUserId ? (
                                <RunningTimer startTime={activeTimer.startTime} />
                            ) : task.time && task.time.total ? (
                                addUpTime(convertMinutesToHours(task.time.total)) + ` Hrs`
                            ) : (
                                "0 Hrs"
                            )}
                        </TimeColumn>
                        <TaskActions>
                            <StatusChip
                                id={task._id}
                                status={task.status}
                                remainingDays={task.remainingDays}
                                parentType="task"
                                onChange={onStatusUpdate}
                                disabled={isLocked || (task.completedDate === null ? false : true)}
                                responsibleId={task.responsible?._id}
                            />
                            {(task.responsible || canAssignTask) && (
                                <UserMenuChip
                                    user={task.responsible}
                                    menuItems={{ reAssign: !isLocked }}
                                    size="small"
                                    onReAssign={!isLocked ? () => onReAssign(task) : undefined}
                                    disabled={isLocked || (task.completedDate === null ? false : true)}
                                />
                            )}
                            {currentUserId === task.responsible?._id && (
                                <TimerButton parentId={task._id} parentType="task" runningTimerId={activeTimer?._id || null} onStartTimer={onStartTimer} onStopTimer={onStopTimer} />
                            )}
                        </TaskActions>
                    </>
                )}
            </TaskBoxItem>
            <TaskDivider />
        </>
    );
};

const ProjectTaskCard = ({ tasks = [], projectId, accountId }) => {
    const [openTasks, setOpenTasks] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);
    const [deletedTasks, setDeletedTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [currentTab, setCurrentTab] = useState("0");
    const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
    const [selectedTaskForAssignment, setSelectedTaskForAssignment] = useState(null);
    const dispatch = useDispatch();
    const [activeTimers, setActiveTimers] = useState({});
    const currentUserId = useSelector(state => state.auth.userId);
    const [isCreateTaskDialogOpen, setIsCreateTaskDialogOpen] = useState(false);
    const [taskToComplete, setTaskToComplete] = useState(null);
    const [confirmCompleteDialogOpen, setConfirmCompleteDialogOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [taskToReopen, setTaskToReopen] = useState(null);
    const [confirmReopenDialogOpen, setConfirmReopenDialogOpen] = useState(false);
    const [taskToRestore, setTaskToRestore] = useState(null);
    const [confirmRestoreDialogOpen, setConfirmRestoreDialogOpen] = useState(false);
    const [gateState, setGateState] = useState(null);
    const timeTracking = useSelector(state => state.ui.timeTracking);
    const hasPermission = useHasPermission();
    const canAddTask = hasPermission('addTasks');
    const canSeeDeleted = hasPermission('canSeeDeleted');
    const project = useSelector(state => state.project.currentProject);
    const isLocked = useIsProjectLocked(project);
    const accountSlug = project?.account?.slug;

    useEffect(() => {
        const openTasks = tasks.filter(task => !task.completedDate && !task.isDeleted);
        const deletedTasks = tasks.filter(task => task.isDeleted)
        const completedTasks = tasks.filter(task => task.completedDate && !task.isDeleted);
        setOpenTasks(openTasks);
        setDeletedTasks(deletedTasks);
        setCompletedTasks(completedTasks);

        // Fetch timers for all open tasks
        const fetchTimers = async () => {
            const newActiveTimers = {};
            for (const task of openTasks) {
                await new Promise(resolve => {
                    dispatch(getTimers('task', task._id, (timers) => {
                        const runningTimer = timers.find(timer => timer.timerRunning && timer.user === currentUserId);
                        if (runningTimer) {
                            newActiveTimers[task._id] = {
                                startTime: new Date(runningTimer.startedAt).getTime(),
                                user: runningTimer.user,
                                _id: runningTimer._id
                            };
                        }
                        resolve();
                    }));
                });
            }
            setActiveTimers(newActiveTimers);
        };

        fetchTimers();
    }, [tasks, dispatch, currentUserId, timeTracking]);

    const startTimerCallback = (timeRecord) => {
        setActiveTimers(prev => ({ ...prev, [timeRecord.parentId]: { startTime: new Date(timeRecord.startedAt).getTime(), user: timeRecord.user, _id: timeRecord._id } }));
    };

    const stopTimerCallback = (timeRecord) => {
        dispatch(triggerRefresh({ triggerName: "project" }));
        setActiveTimers(prev => {
            const { [timeRecord.parentId]: _, ...rest } = prev;
            return rest;
        });

        setOpenTasks((prevTasks) =>
            prevTasks.map((task) =>
                task._id === timeRecord.parentId
                    ? {
                          ...task,
                          time: {
                              ...task.time,
                              total: (task.time.total || 0) + timeRecord.time,
                          },
                      }
                    : task
            )
        );
    };


    const applyStatusResponse = (taskId, newStatus, response) => {
        if (!(response && response.task)) {
            dispatch(showAlert({ severity: "error", message: "Failed to update task status" }));
            return;
        }
        if (newStatus === "In Progress" && response.task.completedDate === null) {
            setCompletedTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
            setOpenTasks(prevTasks => {
                const taskExists = prevTasks.some(task => task._id === taskId);
                return taskExists ? prevTasks : [...prevTasks, response.task];
            });
        }
        setOpenTasks(prevTasks =>
            prevTasks.map(task =>
                task._id === taskId
                    ? { ...task, status: newStatus }
                    : task
            )
        );
        dispatch(showAlert({ severity: "success", message: `Task status updated to ${newStatus}` }));
    };

    const handleStatusChange = (taskId, newStatus) => {
        dispatch(updateTaskStatus(
            taskId,
            newStatus,
            (response) => applyStatusResponse(taskId, newStatus, response),
            (gateResponse) => setGateState({ kind: 'status', taskId, status: newStatus, gate: gateResponse }),
        ));
    };

    const handleBypassGate = () => {
        if (!gateState) return;
        if (gateState.kind === 'status') {
            const { taskId, status } = gateState;
            dispatch(updateTaskStatusWithBypass(
                taskId,
                status,
                (response) => {
                    setGateState(null);
                    applyStatusResponse(taskId, status, response);
                },
                (gateResponse) => setGateState((prev) => prev ? { ...prev, gate: gateResponse } : prev),
            ));
        } else if (gateState.kind === 'reopen') {
            const { taskId, status } = gateState;
            dispatch(updateTaskStatusWithBypass(
                taskId,
                status,
                (response) => {
                    setGateState(null);
                    applyReopenResponse(taskId, response);
                },
                (gateResponse) => setGateState((prev) => prev ? { ...prev, gate: gateResponse } : prev),
            ));
        } else if (gateState.kind === 'complete') {
            const { task } = gateState;
            dispatch(completeTaskWithBypass(
                task._id,
                (response) => {
                    setGateState(null);
                    if (response && response.task) {
                        setOpenTasks(prevTasks => prevTasks.filter((t) => t._id !== task._id));
                        setCompletedTasks(prevTasks => [...prevTasks, response.task]);
                        dispatch(showAlert({ severity: "success", message: "Task completed" }));
                    }
                },
                (gateResponse) => setGateState((prev) => prev ? { ...prev, gate: gateResponse } : prev),
            ));
        }
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleReAssign = useCallback((task) => {
        setSelectedTaskForAssignment(task);
        setAssignmentDialogOpen(true);
    }, []);

    const handleAssignmentDialogClose = () => {
        setAssignmentDialogOpen(false);
        setSelectedTaskForAssignment(null);
    };

    const handleAssign = (taskId, assignees, responsible) => {
        setOpenTasks(prevTasks =>
            prevTasks.map(task =>
                task._id === taskId
                    ? { ...task, assignees, responsible }
                    : task
            )
        );
        setCompletedTasks(prevTasks =>
            prevTasks.map(task =>
                task._id === taskId
                    ? { ...task, assignees, responsible }
                    : task
            )
        );
    };

    const handleOpenCreateTaskDialog = () => {
        setIsCreateTaskDialogOpen(true);
    };

    const handleCloseCreateTaskDialog = () => {
        setIsCreateTaskDialogOpen(false);
    };

    const handleCompleteTask = (task) => {
        setTaskToComplete(task);
        setConfirmCompleteDialogOpen(true);
    };

    const confirmCompleteTask = () => {
        if (taskToComplete) {
            const target = taskToComplete;
            setConfirmCompleteDialogOpen(false);
            setTaskToComplete(null);
            dispatch(completeTask(
                target._id,
                (response) => {
                    if (response && response.task) {
                        setOpenTasks(prevTasks => prevTasks.filter(task => task._id !== target._id));
                        setCompletedTasks(prevTasks => [...prevTasks, response.task]);
                        dispatch(showAlert({ severity: "success", message: "Task completed successfully" }));
                    } else {
                        dispatch(showAlert({ severity: "error", message: "Failed to complete task" }));
                    }
                },
                (gateResponse) => setGateState({ kind: 'complete', task: target, gate: gateResponse }),
            ));
        }
    };

    const handleDeleteTask = (task) => {
        setTaskToDelete(task);
        setConfirmDeleteDialogOpen(true);
    };

    const confirmDeleteTask = () => {
        if (taskToDelete) {
            dispatch(deleteTask(taskToDelete._id, (response) => {
                if (response && response.task) {
                    setOpenTasks(prevTasks => prevTasks.filter(task => task._id !== taskToDelete._id));
                    setCompletedTasks(prevTasks => prevTasks.filter(task => task._id !== taskToDelete._id));
                    setDeletedTasks(prevTasks => [...prevTasks, response.task]);
                    dispatch(showAlert({ severity: "success", message: "Task deleted successfully" }));
                    setSelectedTask(null);
                } else {
                    dispatch(showAlert({ severity: "error", message: "Failed to delete task" }));
                }
                setConfirmDeleteDialogOpen(false);
                setTaskToDelete(null);
            }));
        }
    };

    const handleRestoreTask = (task) => {
        setTaskToRestore(task);
        setConfirmRestoreDialogOpen(true);
    };

    const confirmRestoreTask = () => {
        if (taskToRestore) {
            dispatch(restoreTask(taskToRestore._id, (response) => {
                if (response && response.task) {
                    setDeletedTasks(prevTasks => prevTasks.filter(task => task._id !== taskToRestore._id));
                    if (response.task.completedDate !== null) {
                        setCompletedTasks(prevTasks => [...prevTasks, response.task]);
                    } else {
                        setOpenTasks(prevTasks => [...prevTasks, response.task]);
                    }
                    dispatch(showAlert({ severity: "success", message: "Task restored successfully" }));
                } else {
                    dispatch(showAlert({ severity: "error", message: "Failed to restore task" }));
                }
                setConfirmRestoreDialogOpen(false);
                setTaskToRestore(null);
            }));
        }
    };

    const handleReopenTask = (task) => {
        setTaskToReopen(task);
        setConfirmReopenDialogOpen(true);
    };

    const applyReopenResponse = (taskId, response) => {
        if (response && response.task) {
            setCompletedTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
            setOpenTasks(prevTasks => {
                const exists = prevTasks.some((t) => t._id === taskId);
                return exists ? prevTasks : [...prevTasks, response.task];
            });
            dispatch(showAlert({ severity: "success", message: "Task reopened successfully" }));
        } else {
            dispatch(showAlert({ severity: "error", message: "Failed to reopen task" }));
        }
    };

    const confirmReopenTask = () => {
        if (taskToReopen) {
            const target = taskToReopen;
            setConfirmReopenDialogOpen(false);
            setTaskToReopen(null);
            dispatch(updateTaskStatus(
                target._id,
                "In Progress",
                (response) => applyReopenResponse(target._id, response),
                (gateResponse) => setGateState({ kind: 'reopen', taskId: target._id, status: "In Progress", gate: gateResponse }),
            ));
        }
    };

    return (
        <>
            <TaskCard>
                <TaskCardHeader title={<CardTitleText>Tasks</CardTitleText>} />
                <StyledCardContent>
                    <TabContext value={currentTab}>
                        <StyledTabList onChange={handleTabChange}>
                            <Tab label="Open" value="0" />
                            <Tab label="Completed" value="1" />
                            {canSeeDeleted && <Tab label="Deleted" value="2" />}
                        </StyledTabList>

                        <StyledTabPanel value="0">
                            <TaskBox>
                                {openTasks.map((task) => (
                                    <TaskBoxItems
                                        key={task._id}
                                        task={task}
                                        setSelectedTask={setSelectedTask}
                                        onReAssign={handleReAssign}
                                        onStatusUpdate={handleStatusChange}
                                        activeTimer={activeTimers[task._id]}
                                        onStartTimer={startTimerCallback}
                                        onStopTimer={stopTimerCallback}
                                        currentUserId={currentUserId}
                                        onCompleteTask={handleCompleteTask}
                                        onReopenTask={null}
                                        isLocked={isLocked}
                                        accountSlug={accountSlug}
                                        projectId={projectId}
                                    />
                                ))}
                            </TaskBox>
                        </StyledTabPanel>
                        <StyledTabPanel value="1">
                            <TaskBox>
                                {completedTasks.map((task) => (
                                    <TaskBoxItems
                                        key={task._id}
                                        task={task}
                                        setSelectedTask={setSelectedTask}
                                        onReAssign={handleReAssign}
                                        onStatusUpdate={handleStatusChange}
                                        activeTimer={null}
                                        currentUserId={currentUserId}
                                        onCompleteTask={null}
                                        onReopenTask={handleReopenTask}
                                        isLocked={isLocked}
                                        accountSlug={accountSlug}
                                        projectId={projectId}
                                    />
                                ))}
                            </TaskBox>
                        </StyledTabPanel>
                        <StyledTabPanel value="2">
                            <TaskBox>
                                {deletedTasks.map((task) => (
                                    <TaskBoxItems
                                        key={task._id}
                                        task={task}
                                        setSelectedTask={setSelectedTask}
                                        onReAssign={null}
                                        activeTimer={null}
                                        currentUserId={currentUserId}
                                        onCompleteTask={null}
                                        onRestoreTask={handleRestoreTask}
                                        onReopenTask={null}
                                        isLocked={isLocked}
                                        accountSlug={accountSlug}
                                        projectId={projectId}
                                    />
                                ))}
                            </TaskBox>
                        </StyledTabPanel>
                    </TabContext>
                </StyledCardContent>
                {canAddTask && (
                    <CardActions>
                        <WideButton variant="contained" onClick={handleOpenCreateTaskDialog} disabled={isLocked}>
                            <Add /> Add New Task
                        </WideButton>
                    </CardActions>
                )}
                {selectedTask && (
                    <TaskDetailsDialog
                        task={selectedTask}
                        onClose={() => setSelectedTask(null)}
                        onDeleteTask={() => handleDeleteTask(selectedTask)}
                    />
                )}
                <UserAssignmentDialog
                    isOpen={assignmentDialogOpen}
                    onClose={handleAssignmentDialogClose}
                    objectId={selectedTaskForAssignment?._id}
                    objectType="task"
                    onAssign={handleAssign}
                />
            </TaskCard>

            <CreateTaskDialog
                open={isCreateTaskDialogOpen}
                onClose={handleCloseCreateTaskDialog}
                projectId={projectId}
                accountId={accountId}
            />

            <Dialog open={confirmCompleteDialogOpen} onClose={() => setConfirmCompleteDialogOpen(false)}>
                <DialogTitle>Complete Task</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to mark this task as completed?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCompleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmCompleteTask} variant="contained" color="primary">Complete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
                <DialogTitle>Delete Task</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this task?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDeleteTask} variant="contained" color="primary">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmRestoreDialogOpen} onClose={() => setConfirmRestoreDialogOpen(false)}>
                <DialogTitle>Restore Task</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to restore this task?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRestoreDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmRestoreTask} variant="contained" color="primary">Restore</Button>
                </DialogActions>
            </Dialog>

            <BoardGateWarningDialog
                open={!!gateState}
                gate={gateState?.gate || null}
                onClose={() => setGateState(null)}
                onBypass={handleBypassGate}
                bypassLabel={gateState?.kind === 'complete' ? 'Complete anyway' : gateState?.kind === 'reopen' ? 'Reopen anyway' : 'Move anyway'}
            />

            <Dialog open={confirmReopenDialogOpen} onClose={() => setConfirmReopenDialogOpen(false)}>
                <DialogTitle>Reopen Task</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to reopen this task?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmReopenDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmReopenTask} variant="contained" color="primary">Reopen</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProjectTaskCard;
